import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';

import MDBox from 'components/MDBox';

import Breadcrumbs from 'components/Breadcrumbs';
import {
  navbar,
  navbarContainer,
  navbarMobileMenu,
  navbarRow
} from 'components/Navbars/PortalNavbar/styles'; // Material Dashboard 2 React
import { setMiniSidenav, setTransparentNavbar, useMaterialUIController } from 'context';
import { Stack } from '@mui/material';
import { useAuth } from 'hook/useAuth';
import AccountPopover from 'components/Navbars/PortalNavbar/AccountPopover';
import NotificationsPopover from 'components/Navbars/PortalNavbar/NotificationsPopover';
import GlobalSearchCombobox from 'components/Navbars/PortalNavbar/GlobalSearchCombobox';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import { getImageURL, getUrlParameter } from 'utils/urlUtils';
import app from 'config/app.json';
import MenuIcon from '@mui/icons-material/Menu';
import { useIntl } from 'react-intl';
import { toBoolean } from 'utils/stringUtils';

function PortalNavbar({ view, absolute, light, isMini }) {
  const { formatMessage: msg } = useIntl();

  const { currentUser } = useAuth();

  const [navbarType, setNavbarType] = useState();
  const [previousNavbarTransparent, setPreviousNavbarTransparent] = useState();

  const { controller, dispatch, pageTitle } = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;

  const bannerCode = `banner.${darkMode ? 'dark' : 'light'}`;
  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const [customBanner, setCustomBanner] = useState(conf(`ui.image.${bannerCode}`, null));

  const routePath = useLocation().pathname.split('/').slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType('sticky');
    } else {
      setNavbarType('static');
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      const transparentNavbar = (fixedNavbar && window.scrollY === 0) || !fixedNavbar;
      if (transparentNavbar !== previousNavbarTransparent) {
        setTransparentNavbar(dispatch, transparentNavbar);
      }
      setPreviousNavbarTransparent(transparentNavbar);
    }

    /**
     The event listener that's calling the handleTransparentNavbar function when
     scrolling the window.
     */
    window.addEventListener('scroll', handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [dispatch, fixedNavbar, previousNavbarTransparent]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    }
  });

  return (
    <AppBar
      position={absolute ? 'absolute' : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)} id="toolbar">
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <IconButton
            aria-label={msg({ id: 'action.menu.display' })}
            size="small"
            color="inherit"
            variant="contained"
            sx={navbarMobileMenu}
            onClick={handleMiniSidenav}
          >
            <MenuIcon sx={iconsStyle} fontSize="large" />
          </IconButton>
          <Breadcrumbs
            routePath={routePath}
            view={view}
            light={light}
            pageTitle={pageTitle}
            homeIcon="home"
          />
        </MDBox>
        {customBanner && (
          <MDBox
            component="img"
            src={getImageURL(bannerCode, getUrlParameter(window.location, app.url.paramApp))}
            alt="Logo"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              setCustomBanner(null);
            }}
            sx={{ display: { xs: 'none', md: 'block' }, maxHeight: '4rem' }}
          />
        )}
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {toBoolean(conf(`${app.view.globalSearch}.enabled`), true) && (
              <MDBox pr={1}>
                <GlobalSearchCombobox />
              </MDBox>
            )}
            <MDBox color={light ? 'white' : 'inherit'}>
              <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
                {currentUser.user && (
                  <>
                    <NotificationsPopover iconsStyle={iconsStyle} /> <AccountPopover />
                  </>
                )}
              </Stack>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

PortalNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false
};

PortalNavbar.propTypes = {
  view: PropTypes.string,
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool
};

export default PortalNavbar;
